import { Clock4, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import type { CoachNote } from "@/hooks/useCoachNotes";

interface CoachNotesSectionProps {
  notesByDate: Array<[string, CoachNote[]]>;
  newNoteText: string;
  onNewNoteTextChange: (value: string) => void;
  onSaveNote: () => void;
  isAdding: boolean;
  editingNoteId: string | null;
  editingText: string;
  onEditingTextChange: (value: string) => void;
  onStartEditing: (id: string, content: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: () => void;
  onDeleteNote: (id: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  formatTimestamp: (value?: string | null) => string;
}

export function CoachNotesSection({
  notesByDate,
  newNoteText,
  onNewNoteTextChange,
  onSaveNote,
  isAdding,
  editingNoteId,
  editingText,
  onEditingTextChange,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
  onDeleteNote,
  isUpdating,
  isDeleting,
  formatTimestamp,
}: CoachNotesSectionProps) {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-h3">Coach Notes</CardTitle>
        <p className="text-sm text-muted-foreground">Multiple notes per day, grouped by date, private to coach.</p>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="space-y-2">
          <Textarea
            value={newNoteText}
            onChange={(event) => onNewNoteTextChange(event.target.value)}
            placeholder="Write a prep note..."
            className="min-h-[110px]"
          />
          <div className="flex justify-end">
            <Button onClick={onSaveNote} disabled={isAdding || !newNoteText.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {isAdding ? "Saving..." : "Save Note"}
            </Button>
          </div>
        </div>

        {notesByDate.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes saved yet.</p>
        ) : (
          notesByDate.map(([dateLabel, dateNotes]) => (
            <div key={dateLabel} className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Clock4 className="h-3 w-3" /> {dateLabel}
              </p>
              {dateNotes.map((note) => (
                <div key={note.id} className="rounded-lg border border-border/60 p-3 space-y-2">
                  <p className="text-xs text-muted-foreground">{formatTimestamp(note.created_at)}</p>
                  {editingNoteId === note.id ? (
                    <div className="space-y-2">
                      <Textarea value={editingText} onChange={(event) => onEditingTextChange(event.target.value)} className="min-h-[90px]" />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={onCancelEditing}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={onSaveEdit} disabled={isUpdating || !editingText.trim()}>
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{note.content}</p>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => onStartEditing(note.id, note.content)}>
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDeleteNote(note.id)}
                          disabled={isDeleting}
                        >
                          Delete
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
